import React from "react";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";
import Button from "@directv-digital/fusion.atoms.button";
import { CtaSectionProps } from "../package-carousel.props";
import { DtvTheme } from "@directv-digital/dds.ui.dtv-theme/dist/dtv-theme";
import { absoluteLinkUrl, getLinkClickAttrs, modalIdFromUrl, modalLinkDestination, usePageUrl } from "src/utils/link-click";

const bps = DtvTheme.breakpoints;

const styles = {
  ctaStyles: {
    "&:before": {
      border: "0px solid transparent !important"
    },
    width: {
      xs: "100%",
      sm: "287px",
      md: "335px"
    },
    margin: "0",
    [bps.down(bps.values.sm)]: {
      width: "100% !important"
    },
    [bps.up(bps.values.sm)]: {
      width: "287px !important"
    },
    [bps.up(bps.values.md)]: {
      width: "335px !important"
    },
    "& span": {
      fontFamily: "roboto-medium"
    }
  }
};

const CtaSection = (props: CtaSectionProps) => {
  const { cta, secondaryCta } = props;
  // WAT-12797 (AC6/AC8/AC9): lower-case name and an absolute destination — the CTA
  // URL, or page URL + "/<modal id>" for a CTA that opens a modal. Rendered after the
  // CMS spread so a raw label can't replace them.
  const pageUrl = usePageUrl();
  const secondaryModalType = secondaryCta?.modalType || "iframe";
  const ctaAttrs = getLinkClickAttrs(cta?.label, "genrepack_carousel", absoluteLinkUrl(cta?.targeturl, pageUrl));
  const secondaryCtaAttrs = getLinkClickAttrs(
    secondaryCta?.label,
    "genrepack_carousel",
    secondaryCta?.modalData?.url ?
      modalLinkDestination(pageUrl, modalIdFromUrl(secondaryCta.modalData.url, secondaryModalType)) :
      absoluteLinkUrl(secondaryCta?.targeturl, pageUrl)
  );
  try {
    return (
      <>
        {cta?.label && (
          // @ts-expect-error Fusion Button prop typing is stricter than the CMS-driven props spread here
          <Button
            buttontype={cta?.buttontype || "primary"}
            googleanalyticsdata={ctaAttrs}
            href={cta?.targeturl}
            mode={cta?.mode || "light"}
            size={cta?.size || "large"}
            tabIndex={0}
            sx={{
              color: cta?.color,
              background: cta?.background,
              "&:hover, &:active, &:focus": {
                color: cta?.hover?.color,
                background: cta?.hover?.background,
                border: cta?.hover?.border
              },
              ...styles.ctaStyles
            }}
            {...cta}
            aria-label={ctaAttrs["aria-label"] || cta?.label}
            data-linkposition={ctaAttrs["data-linkposition"]}
            data-linkdestination={ctaAttrs["data-linkdestination"]}
          >
            {cta?.label}
          </Button>
        )}
        {secondaryCta?.label && (
          // @ts-expect-error Fusion Button prop typing is stricter than the CMS-driven props spread here
          <Button
            buttontype={secondaryCta?.buttontype || "secondary"}
            googleanalyticsdata={secondaryCtaAttrs}
            mode={secondaryCta?.mode || "light"}
            size={secondaryCta?.size || "large"}
            tabIndex={0}
            sx={{
              color: secondaryCta?.color,
              background: secondaryCta?.background,
              border: secondaryCta?.border,
              "&:hover, &:active, &:focus": {
                color: secondaryCta?.hover?.color,
                background: secondaryCta?.hover?.background,
                border: secondaryCta?.hover?.border
              },
              ...styles.ctaStyles
            }}
            {...secondaryCta}
            {...(secondaryCta?.modalData?.url ?
              {
                  modalData: { url: secondaryCta?.modalData?.url },
                  modalType: secondaryModalType
                } :
              { targeturl: secondaryCta?.targeturl })}
            aria-label={secondaryCtaAttrs["aria-label"] || secondaryCta?.label}
            data-linkposition={secondaryCtaAttrs["data-linkposition"]}
            data-linkdestination={secondaryCtaAttrs["data-linkdestination"]}
          >{secondaryCta?.label}</Button>)}
      </>
    );
  } catch (error) {
    return <ErrorDisplay component="CtaSection" error={error} />;
  }
};

export default React.memo(CtaSection);
